import { useEffect, useState } from "react";
import { toast } from "sonner";
import client, { imgUrl, logoUrl, API } from "@/lib/glossyApi";
import {
  Globe, RefreshCw, Save, Upload, X, ScanFace, Check, Plus, FileText, Trash2, Sparkles, Eye, Camera,
  Image as ImageIcon,
} from "lucide-react";
import { ARTICLE_TYPE_LABEL as PREVIEW_TYPE_LABEL } from "@/lib/articleTypes";

/**
 * The Master section — the single source of truth at the top of every Member Studio page.
 * Website scan (+ extra URLs) → company file → own words → reference docs → key facts →
 * ideal customer → basics → social handles → opening hours → logo → master photos (3-5).
 * Every AI action anywhere else in the Studio anchors on this section.
 */
export default function MasterSection({ member, setMember, onChange, saveFields, busy: outerBusy, articles, inUse = {}, onArticlesChange }) {
  const m = member;
  const set = (k, v) => setMember({ ...m, [k]: v });

  const [scanUrl, setScanUrl] = useState(m.website || "");
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [pickedFields, setPickedFields] = useState({});
  const [pickedPhotos, setPickedPhotos] = useState({});
  const [pickedLogo, setPickedLogo] = useState(false);
  const [busy, setBusy] = useState("");
  const [refDocs, setRefDocs] = useState([]);
  const [magEvent, setMagEvent] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planChoices, setPlanChoices] = useState({});
  const [harvest, setHarvest] = useState(null);
  const [pickedHarvest, setPickedHarvest] = useState({});
  const [harvesting, setHarvesting] = useState(false);
  const [productScene, setProductScene] = useState("");

  const combineProduct = async (p, withMember) => {
    setBusy(`${withMember ? "combine" : "hero"}-${p}`);
    try {
      const r = await client.post(`/admin/members/${m.id}/product-shot`, { product: p, with_member: withMember, scene: productScene });
      setMember({ ...m, shot_gallery: r.data.shot_gallery });
      toast.success(withMember ? "Shot made — them with their exact product. It's in AI Images Saved below." : "Hero product shot made — it's in AI Images Saved below.");
    } catch (e) { toast.error(e.response?.data?.detail || "The studio couldn't get that shot — try again."); }
    setBusy("");
  };

  const runHarvest = async () => {
    const u = (scanUrl || m.website || "").trim();
    if (!u) { toast.error("Add their website address first."); return; }
    setHarvesting(true);
    try {
      const r = await client.post(`/admin/members/${m.id}/harvest-images`, { url: u });
      setHarvest(r.data.images);
      setPickedHarvest({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't read photos from that site.");
    }
    setHarvesting(false);
  };

  const importHarvest = async () => {
    const urls = Object.entries(pickedHarvest).filter(([, on]) => on).map(([u]) => u);
    if (!urls.length) return;
    setBusy("harvest-import");
    try {
      const r = await client.post(`/admin/members/${m.id}/import-photos`, { urls });
      if (!r.data.imported) {
        toast.error("That site blocked the downloads — ask the member for the original files.");
      } else {
        setMember({ ...m, photos: r.data.photos });
        toast.success(`${r.data.imported} photo${r.data.imported === 1 ? "" : "s"} added to Master Photos — their real imagery now feeds the whole magazine.`);
        setHarvest(null);
        onChange && onChange();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "That site blocked the download — ask the member for the original files.");
    }
    setBusy("");
  };

  const toggleNoAi = async (on) => {
    try {
      await client.patch(`/admin/members/${m.id}`, { no_ai_photos: on });
      setMember({ ...m, no_ai_photos: on });
      const defaults = {};
      (plan?.slots || []).forEach((s) => { defaults[s.key] = on ? false : planChoices[s.key]; });
      if (plan) { setPlanChoices(defaults); setPlan({ ...plan, own_photos_only: on || plan.imagery_preference === "own", no_ai_photos: on }); }
      toast.success(on ? "AI photos OFF — only their own photos will ever be used." : "AI photos back on for this member.");
    } catch { toast.error("Couldn't save that."); }
  };

  const binAllShots = async () => {
    if (!window.confirm("Bin ALL saved AI images for this member?\n\nAnything currently in live use (covers, advert, article images) is kept. The rest are removed permanently.")) return;
    setBusy("bin-all");
    try {
      const r = await client.post(`/admin/members/${m.id}/shot-gallery/clear`, {});
      setMember({ ...m, shot_gallery: r.data.shot_gallery });
      toast.success(`${r.data.binned} image${r.data.binned === 1 ? "" : "s"} binned. ${r.data.shot_gallery.length} in-use image${r.data.shot_gallery.length === 1 ? "" : "s"} kept.`);
      onChange && onChange();
    } catch { toast.error("Couldn't bin the images."); }
    setBusy("");
  };

  const [previewSetup, setPreviewSetup] = useState(null);
  const [previewTicks, setPreviewTicks] = useState({});
  const [previewSections, setPreviewSections] = useState({ show_advert: true, show_directory: true, show_notice: true, show_socials: true });
  const [previewHas, setPreviewHas] = useState({});

  const copyPreviewLink = async () => {
    setBusy("preview-link");
    try {
      const r = await client.get(`/admin/members/${m.id}/preview-setup`);
      const inc = r.data.include || {};
      const ticks = {};
      r.data.articles.forEach((a) => { ticks[a.id] = inc.article_ids ? inc.article_ids.includes(a.id) : true; });
      setPreviewTicks(ticks);
      setPreviewSections({
        show_advert: inc.show_advert !== false, show_directory: inc.show_directory !== false,
        show_notice: inc.show_notice !== false, show_socials: inc.show_socials !== false,
      });
      setPreviewHas(r.data.sections || {});
      setPreviewSetup(r.data.articles);
    } catch { toast.error("Couldn't load the preview setup."); }
    setBusy("");
  };

  const sharePreview = async () => {
    setBusy("preview-link");
    try {
      const ids = Object.entries(previewTicks).filter(([, on]) => on).map(([id]) => id);
      const all = previewSetup.length === ids.length;
      const r = await client.post(`/admin/members/${m.id}/preview-link`, { include: { article_ids: all ? null : ids, ...previewSections } });
      const url = `${window.location.origin}/preview/${r.data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(`Preview link copied — the member sees ${all ? "every article" : `${ids.length} of ${previewSetup.length} articles`} plus the extras you ticked.`);
      setPreviewSetup(null);
    } catch { toast.error("Couldn't make the preview link."); }
    setBusy("");
  };

  useEffect(() => {
    client.get(`/admin/members/${m.id}/ref-docs`).then((r) => setRefDocs(r.data.ref_docs || [])).catch(() => {});
  }, [m.id]);

  // maps scan basic keys → member document keys
  const fieldMap = { tagline: "category" };

  const runScan = async () => {
    const u = (scanUrl || "").trim();
    if (!u) { toast.error("Type their website address first."); return; }
    setScanning(true);
    setScan(null);
    try {
      const r = await client.post(`/admin/members/${m.id}/master-scan`, { url: u });
      const data = r.data;
      setScan(data);
      const picks = {};
      Object.entries(data.basics || {}).forEach(([k, v]) => {
        const currentlyEmpty = !(m[fieldMap[k] || k] || "").toString().trim();
        picks[k] = !!(v && v.trim()) && currentlyEmpty;
      });
      setPickedFields(picks);
      const cap = 5 - ((m.photos || []).length);
      const ticked = {};
      (data.candidate_photos || []).slice(0, Math.max(0, cap)).forEach((u) => { ticked[u] = true; });
      setPickedPhotos(ticked);
      setPickedLogo(!!data.logo_url && !m.logo);
      toast.success("Site read. Untick anything you don't want, then apply.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read that website.");
    }
    setScanning(false);
  };

  const applyBasics = () => {
    if (!scan) return;
    const next = { ...m };
    Object.entries(scan.basics || {}).forEach(([k, v]) => {
      if (!pickedFields[k] || !v || !v.toString().trim()) return;
      const targetKey = fieldMap[k] || k;
      const currentlyEmpty = !((next[targetKey] || "").toString().trim());
      if (currentlyEmpty) next[targetKey] = v.toString().trim();
    });
    setMember(next);
    toast.success("Empty fields filled from the scan. Hit Save to keep.");
  };

  const applyPhotos = async () => {
    const urls = Object.entries(pickedPhotos).filter(([_, on]) => on).map(([u]) => u);
    if (urls.length === 0) { toast.error("Tick at least one photo to import."); return; }
    setBusy("photos");
    try {
      const r = await client.post(`/admin/members/${m.id}/master-apply-photos`, { urls });
      setMember({ ...m, photos: r.data.photos });
      toast.success(`${r.data.imported} photo${r.data.imported === 1 ? "" : "s"} imported to Master Photos.`);
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Photo import failed."); }
    setBusy("");
  };

  const applyLogo = async () => {
    if (!scan?.logo_url) return;
    setBusy("logo");
    try {
      const r = await client.post(`/admin/members/${m.id}/master-apply-logo`, { url: scan.logo_url });
      setMember({ ...m, logo: r.data.logo, accent_color: r.data.accent_color || m.accent_color });
      toast.success("Logo set from the scan.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Logo import failed."); }
    setBusy("");
  };

  const uploadPhotos = async (files) => {
    if (!files || files.length === 0) return;
    const remaining = 5 - (m.photos || []).length;
    if (remaining <= 0) { toast.error("Master Photos already at the 5-photo cap. Remove one first."); return; }
    const fd = new FormData();
    Array.from(files).slice(0, remaining).forEach((f) => fd.append("files", f));
    setBusy("upload");
    try {
      const r = await client.post(`/admin/members/${m.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMember({ ...m, photos: r.data.photos });
      toast.success("Photo(s) uploaded.");
      onChange && onChange();
    } catch { toast.error("Upload failed."); }
    setBusy("");
  };

  const removePhoto = async (path) => {
    setBusy("remove");
    try {
      const r = await client.post(`/admin/members/${m.id}/photos/remove`, { path, field: "photos" });
      const next = { ...m, photos: r.data.photos };
      if (m.likeness_ref === path) {
        await client.post(`/admin/members/${m.id}/likeness`, { path: null });
        next.likeness_ref = null;
      }
      setMember(next);
      toast.success("Photo removed.");
      onChange && onChange();
    } catch { toast.error("Couldn't remove that photo."); }
    setBusy("");
  };

  const setFaceLock = async (path) => {
    setBusy("lock");
    try {
      const next = m.likeness_ref === path ? null : path;
      await client.post(`/admin/members/${m.id}/likeness`, { path: next });
      setMember({ ...m, likeness_ref: next });
      toast.success(next ? "Face Lock set — every AI shoot will copy this face." : "Face Lock cleared.");
      onChange && onChange();
    } catch { toast.error("Couldn't set Face Lock."); }
    setBusy("");
  };

  const uploadLogo = async (files) => {
    if (!files || files.length === 0) return;
    const fd = new FormData();
    fd.append("file", files[0]);
    setBusy("logo-upload");
    try {
      const r = await client.post(`/admin/members/${m.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMember({ ...m, logo: r.data.logo, accent_color: r.data.accent_color || m.accent_color });
      toast.success("Logo replaced.");
      onChange && onChange();
    } catch { toast.error("Logo upload failed."); }
    setBusy("");
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the logo?")) return;
    setBusy("logo-remove");
    try {
      await client.patch(`/admin/members/${m.id}`, { logo: null });
      setMember({ ...m, logo: null });
      toast.success("Logo removed.");
      onChange && onChange();
    } catch { toast.error("Couldn't remove logo."); }
    setBusy("");
  };

  const uploadRefDocs = async (files) => {
    if (!files || files.length === 0) return;
    const fd = new FormData();
    Array.from(files).slice(0, 5).forEach((f) => fd.append("files", f));
    setBusy("refdocs");
    try {
      const r = await client.post(`/admin/members/${m.id}/ref-docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRefDocs(r.data.ref_docs || []);
      toast.success("Reference document(s) uploaded — the writers will read them.");
      onChange && onChange();
    } catch { toast.error("Upload failed."); }
    setBusy("");
  };

  const removeRefDoc = async (id) => {
    if (!window.confirm("Remove this reference document?")) return;
    setBusy("refdoc-remove");
    try {
      const r = await client.delete(`/admin/members/${m.id}/ref-docs/${id}`);
      setRefDocs(r.data.ref_docs || []);
      toast.success("Reference document removed.");
      onChange && onChange();
    } catch { toast.error("Couldn't remove that document."); }
    setBusy("");
  };

  const assignShotToSlot = async (img, target) => {
    setBusy("assign");
    try {
      if (target.startsWith("article:")) {
        const kind = target.split(":")[1];
        const art = (articles || {})[kind];
        if (!art) { toast.error("Nothing written for that panel yet — run Master Build or draft it first."); setBusy(""); return; }
        await client.patch(`/admin/articles/${art.id}`, { cover_image: img });
        toast.success(`Now the ${ARTICLE_LABELS[kind]} cover.`);
        onArticlesChange && onArticlesChange();
      } else {
        const r = await client.post(`/admin/members/${m.id}/shot-gallery/assign`, { image: img, target });
        const updated = r.data.member || {};
        setMember({ ...m, ...updated });
        toast.success(`Assigned to ${target.replace(":", " → ").replace("_", " ")}`);
        onArticlesChange && onArticlesChange();
      }
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't assign."); }
    setBusy("");
  };

  const deleteShot = async (id, usedAs) => {
    const msg = usedAs
      ? `This image is IN USE as ${usedAs}. Delete it anyway? It will be removed from there too — that slot goes empty until you set a new image.`
      : "Delete this saved image permanently?";
    if (!window.confirm(msg)) return;
    setBusy("del-shot");
    try {
      const r = await client.post(`/admin/members/${m.id}/shot-gallery/remove`, { id, everywhere: !!usedAs });
      setMember({ ...m, ...(r.data.member || {}), shot_gallery: r.data.shot_gallery });
      toast.success(usedAs ? "Deleted — and cleared from everywhere it was used." : "Image deleted.");
      onChange && onChange();
      if (usedAs && onArticlesChange) onArticlesChange();
    } catch { toast.error("Couldn't delete."); }
    setBusy("");
  };

  const generateWholeMagazine = async (force = false) => openPhotoPlan(force, false);

  const openPhotoPlan = async (force = false, standalone = false) => {
    try {
      const r = await client.get(`/admin/members/${m.id}/photo-plan`);
      const p = r.data || {};
      const saved = m.photo_choices || {};
      const defaults = {};
      (p.slots || []).forEach((s) => { defaults[s.key] = p.own_photos_only ? false : saved[s.key] !== "skip"; });
      setPlanChoices(defaults);
      setPlan({ ...p, force, standalone });
    } catch { toast.error("Couldn't load the photo plan."); }
  };

  const savePhotoChoices = async () => {
    const photo_choices = {};
    Object.entries(planChoices).forEach(([k, on]) => { photo_choices[k] = on ? "generate" : "skip"; });
    try {
      await client.patch(`/admin/members/${m.id}`, { photo_choices });
      setMember({ ...m, photo_choices });
      setPlan(null);
      toast.success("Photo choices saved — every future build for this member follows them.");
    } catch { toast.error("Couldn't save that."); }
  };

  const startWholeMagazine = async (force = false) => {
    const image_choices = {};
    Object.entries(planChoices).forEach(([k, on]) => { image_choices[k] = on ? "generate" : "skip"; });
    setPlan(null);
    setBusy("whole-mag");
    setMagEvent({ done: [], skipped: [], failed: [], current: "Starting…", running: true });
    try {
      const r = await client.post(`/admin/members/${m.id}/generate-whole-magazine`, { force, image_choices });
      if (r.data?.status === "already_running") toast.info("A whole-magazine build is already in progress for this member.");
      const poll = async () => {
        try {
          const s = await client.get(`/admin/members/${m.id}/magazine-job`);
          const st = s.data || {};
          setMagEvent({ done: st.steps_done || [], skipped: st.steps_skipped || [], failed: st.steps_failed || [], current: st.current_step || "", running: st.status === "running" });
          if (st.status === "running") {
            setTimeout(poll, 3000);
          } else if (st.status === "done") {
            const doneN = (st.steps_done || []).length;
            const failN = (st.steps_failed || []).length;
            const skipN = (st.steps_skipped || []).length;
            if (doneN && !failN) toast.success(`Whole magazine ${force ? "rebuilt" : "built"} — ${doneN} new${skipN ? `, ${skipN} kept` : ""}.`);
            else if (!doneN && skipN && !failN) toast.success(`Nothing missing — all ${skipN} items already made.`);
            else if (failN) toast.error(`Finished with ${failN} failure${failN === 1 ? "" : "s"}.`);
            else toast.success("Done.");
            setBusy("");
            try { const mm = await client.get(`/admin/members/${m.id}`); setMember(mm.data); } catch { /* ignore */ }
            onChange && onChange();
          }
        } catch { setBusy(""); }
      };
      setTimeout(poll, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Whole magazine failed to start.");
      setBusy("");
    }
  };

  const photoCount = (m.photos || []).length;
  const facelockOk = !!m.likeness_ref && photoCount >= 3;

  return (
    <section id="section-master" data-testid="master-section" className="space-y-10 scroll-mt-24">
      {/* ============ Header banner ============ */}
      <div className="bg-ink text-cream px-5 py-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 grain relative">
        <span className="overline text-gloss-gold relative z-10">Master · the single source of truth for every article, image and post</span>
      </div>

      {/* ============ 1 · Website scan ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="master-scan-panel">
        <p className="overline text-ink/60 mb-2">1 · Website scan</p>
        <p className="font-sans text-sm text-ink/60 mb-4">Read the member&apos;s website and pull business info, photos and their logo. <b>Nothing is saved until you tick and apply.</b> Existing fields are never overwritten — the scan only fills empty ones.</p>
        <div className="flex flex-wrap gap-2">
          <input type="url" value={scanUrl} onChange={(e) => setScanUrl(e.target.value)} placeholder="https://theirbusiness.co.uk"
            className="flex-1 min-w-[240px] border border-ink/30 px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-ink" data-testid="scan-url" />
          <button onClick={runScan} disabled={scanning || !!busy} className="bg-ink text-cream px-4 py-2.5 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="scan-run">
            {scanning ? <RefreshCw size={14} className="animate-spin" /> : <Globe size={14} />} Scan website
          </button>
          <button onClick={runHarvest} disabled={harvesting || !!busy} className="border-2 border-ink text-ink px-4 py-2.5 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="harvest-run"
            title="Pulls every usable photo off their site — products, premises, team — so their real imagery fills the magazine instead of AI.">
            {harvesting ? <RefreshCw size={14} className="animate-spin" /> : <Camera size={14} />} Harvest their photos
          </button>
        </div>

        {harvest && (
          <div className="mt-6 border-2 border-ink p-4 space-y-3" data-testid="harvest-results">
            <div className="flex items-start justify-between gap-3">
              <p className="overline text-ink/60">Photos found on their site ({harvest.length}) — tick the ones to keep as Master Photos</p>
              <button onClick={() => setHarvest(null)} className="p-1 text-ink/40 hover:text-ink" data-testid="harvest-close"><X size={14} /></button>
            </div>
            <p className="font-sans text-xs text-ink/55">Perfect for members who don&apos;t want their face used — pick their products or premises instead. <b>Print ready</b> means big enough for full-page spreads; smaller ones still work for cards and galleries.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {harvest.map((im) => (
                <label key={im.url} className={`relative border cursor-pointer overflow-hidden group ${pickedHarvest[im.url] ? "border-gloss-gold border-2" : "border-ink/15"}`} data-testid={`harvest-photo-${im.url.slice(-16)}`}>
                  <input type="checkbox" checked={!!pickedHarvest[im.url]} onChange={(e) => setPickedHarvest({ ...pickedHarvest, [im.url]: e.target.checked })} className="absolute top-1.5 left-1.5 z-10" />
                  <div className="aspect-square bg-ink/5">
                    <img src={im.url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" onError={(e) => { e.currentTarget.style.opacity = 0.15; }} />
                  </div>
                  <p className={`absolute bottom-0 inset-x-0 px-1.5 py-0.5 font-mono text-[9px] ${im.print_ready ? "bg-gloss-gold text-ink" : "bg-ink/70 text-cream"}`}>
                    {im.width}×{im.height}{im.print_ready ? " · print ready" : ""}
                  </p>
                </label>
              ))}
            </div>
            <button onClick={importHarvest} disabled={busy === "harvest-import" || !Object.values(pickedHarvest).some(Boolean)}
              className="bg-gloss-gold text-ink px-5 py-2.5 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-50" data-testid="harvest-import">
              {busy === "harvest-import" ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />} Add ticked to Master Photos
            </button>
          </div>
        )}

        {scan && (
          <div className="mt-6 border border-ink/15 p-4 space-y-4" data-testid="scan-results">
            <p className="overline text-ink/60">Scan results — untick anything wrong; empty fields only will be filled</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(scan.basics || {}).map(([k, v]) => {
                if (!v || !v.toString().trim()) return null;
                const targetKey = fieldMap[k] || k;
                const currentlyEmpty = !((m[targetKey] || "").toString().trim());
                const label = LABELS[k] || k;
                return (
                  <label key={k} className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${pickedFields[k] ? "border-gloss-gold bg-gloss-gold/5" : "border-ink/15"}`} data-testid={`scan-field-${k}`}>
                    <input type="checkbox" checked={!!pickedFields[k]} onChange={(e) => setPickedFields({ ...pickedFields, [k]: e.target.checked })}
                      disabled={!currentlyEmpty} className="mt-1 shrink-0" data-testid={`scan-tick-${k}`} />
                    <div className="min-w-0 flex-1">
                      <p className="overline text-[10px] text-ink/60 mb-1">{label}{!currentlyEmpty && <span className="ml-2 text-ink/40">· already set — skipped</span>}</p>
                      <p className="font-sans text-sm text-ink break-words">{v}</p>
                    </div>
                  </label>
                );
              })}
            </div>
            <button onClick={applyBasics} className="border border-ink px-4 py-2 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors" data-testid="scan-apply-basics">
              <Check size={13} /> Fill ticked empty fields
            </button>

            {(scan.candidate_photos || []).length > 0 && (
              <div className="pt-4 border-t border-ink/10">
                <p className="overline text-[10px] text-ink/60 mb-2">Candidate photos ({(scan.candidate_photos || []).length}) — tick up to {Math.max(0, 5 - photoCount)} to add to Master Photos</p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {(scan.candidate_photos || []).map((u) => (
                    <label key={u} className={`relative aspect-square border cursor-pointer overflow-hidden ${pickedPhotos[u] ? "border-gloss-gold border-2" : "border-ink/15"}`} data-testid={`scan-photo-${u.slice(-20)}`}>
                      <input type="checkbox" checked={!!pickedPhotos[u]} onChange={(e) => setPickedPhotos({ ...pickedPhotos, [u]: e.target.checked })} className="absolute top-1 left-1 z-10" />
                      <img src={u} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
                    </label>
                  ))}
                </div>
                <button onClick={applyPhotos} disabled={busy === "photos" || photoCount >= 5} className="mt-3 border border-ink px-4 py-2 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="scan-apply-photos">
                  {busy === "photos" ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />} Import ticked photos {photoCount >= 5 && "(5-photo cap reached)"}
                </button>
              </div>
            )}

            {scan.logo_url && (
              <div className="pt-4 border-t border-ink/10 flex items-center gap-3 flex-wrap" data-testid="scan-logo-block">
                <label className={`flex items-center gap-2 border p-2 cursor-pointer ${pickedLogo ? "border-gloss-gold" : "border-ink/15"}`}>
                  <input type="checkbox" checked={pickedLogo} onChange={(e) => setPickedLogo(e.target.checked)} data-testid="scan-tick-logo" />
                  <img src={scan.logo_url} alt="" className="h-8 max-w-[120px] object-contain" referrerPolicy="no-referrer" />
                  <span className="overline text-[10px] text-ink/60">candidate logo</span>
                </label>
                <button onClick={applyLogo} disabled={!pickedLogo || busy === "logo"} className="border border-ink px-4 py-2 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="scan-apply-logo">
                  {busy === "logo" ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />} Use this logo {m.logo && "(replaces existing)"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============ 2 · Master photos + Face Lock ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="master-photos-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <p className="overline text-ink/60">2 · Master photos ({photoCount}/5) + Face Lock</p>
            <p className="font-sans text-sm text-ink/60 mt-1">The raw photos every AI image copies from. <b>3 minimum, 5 maximum.</b> Pick one as <b>Face Lock</b> — every future AI shoot copies that face exactly.</p>
            <p className={`font-mono text-[11px] mt-2 px-2 py-1 inline-block ${m.imagery_preference === "own" ? "bg-gloss-gold/20 text-ink" : "bg-ink/5 text-ink/60"}`} data-testid="member-imagery-choice">
              Member&apos;s choice: {m.imagery_preference === "own" ? "USE THEIR PHOTOS AS THEY ARE — no styled generation" : m.imagery_preference === "studio" ? "Studio styles their shots" : "not chosen yet"}
            </p>
          </div>
          <label className="border border-ink/40 px-4 py-2.5 overline flex items-center gap-2 cursor-pointer hover:border-ink transition-colors" data-testid="upload-photos">
            {busy === "upload" ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />} Upload photos
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(e.target.files)} disabled={!!busy || photoCount >= 5} />
          </label>
        </div>

        {photoCount === 0 ? (
          <p className="font-mono text-xs text-ink/45 border border-dashed border-ink/20 p-6 text-center" data-testid="photos-empty">No photos yet — upload 3 to 5, or import from a website scan above.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {(m.photos || []).map((p, i) => {
              const isLock = m.likeness_ref === p;
              return (
                <div key={p} className={`relative aspect-square bg-secondary overflow-hidden ${isLock ? "border-2 border-gloss-gold" : "border border-ink/15"}`} data-testid={`master-photo-${i}`}>
                  <img src={imgUrl({ cover_image: p })} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removePhoto(p)} disabled={!!busy} className="absolute top-1 right-1 bg-ink/80 text-cream p-1 hover:bg-red-700 transition-colors" title="Remove permanently" data-testid={`master-photo-remove-${i}`}>
                    <X size={12} />
                  </button>
                  <button onClick={() => setFaceLock(p)} disabled={!!busy} className={`absolute bottom-1 left-1 p-1 transition-colors ${isLock ? "bg-gloss-gold text-ink" : "bg-ink/80 text-cream hover:bg-gloss-gold hover:text-ink"}`}
                    title={isLock ? "Face lock is on this photo — click to unlock" : "Lock the face to this photo"} data-testid={`master-photo-lock-${i}`}>
                    <ScanFace size={12} />
                  </button>
                  {isLock && <span className="absolute bottom-1 right-1 overline text-[9px] bg-gloss-gold text-ink px-1.5 py-0.5">Face lock</span>}
                </div>
              );
            })}
          </div>
        )}

        <div className={`mt-4 border p-3 text-sm ${facelockOk ? "border-green-600/40 bg-green-600/5" : "border-gloss-gold/50 bg-gloss-gold/10"}`} data-testid="facelock-status">
          {facelockOk
            ? <span className="font-sans text-ink flex items-center gap-2"><Check size={15} className="text-green-600" /> Ready — Face Lock set on the marked photo. AI shoots will copy that face.</span>
            : <span className="font-sans text-ink flex items-center gap-2"><ScanFace size={15} className="text-gloss-gold" /> AI image generation is <b>blocked</b> until you have <b>at least 3 photos</b> and <b>one is Face-Locked</b>. Currently: {photoCount}/3 photos · Face lock {m.likeness_ref ? "SET" : "NOT SET"}.</span>}
        </div>
      </div>

      {/* ============ Photo Controls (standalone — no build needed) ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="photo-controls-panel">
        <p className="overline text-ink/60 mb-2">Photo Controls</p>
        <p className="font-sans text-sm text-ink/60 mb-4">Decide how this member&apos;s photography works — on its own, no build required. Choices are saved and every future build follows them.</p>
        <div className="flex flex-wrap gap-2 items-center">
          <button onClick={() => openPhotoPlan(false, true)} disabled={!!busy} className="border-2 border-ink text-ink px-4 py-2.5 overline text-xs flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-50" data-testid="open-photo-plan-btn">
            <ImageIcon size={13} /> Open the photo plan
          </button>
          <label className={`flex items-center gap-2 border-2 px-4 py-2.5 cursor-pointer transition-colors ${m.no_ai_photos ? "border-gloss-gold bg-gloss-gold/10" : "border-ink/20"}`} data-testid="photo-controls-no-ai">
            <input type="checkbox" checked={!!m.no_ai_photos} onChange={(e) => toggleNoAi(e.target.checked)} />
            <span className="overline text-xs text-ink">No AI photos — use their own only</span>
          </label>
        </div>
      </div>

      {/* ============ Member Preview & Sign-off (standalone) ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="member-preview-panel">
        <p className="overline text-ink/60 mb-2">Member Preview &amp; Sign-off</p>
        <p className="font-sans text-sm text-ink/60 mb-4">A private link showing the member their S/ME package — articles open in the real magazine layout, even before they&apos;re live. You choose exactly which parts they see. They press Approve or Request changes and it lands in your Leads inbox. Nothing publishes until you say so.</p>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={copyPreviewLink}
            disabled={!!busy}
            className="bg-ink text-cream px-5 py-3 overline text-xs flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-40"
            data-testid="copy-preview-link-btn"
          >
            <Eye size={13} /> {busy === "preview-link" ? "Loading…" : "Share their preview"}
          </button>
          {m.preview_response && (
            <span className={`self-center font-mono text-[10px] px-2.5 py-1.5 ${m.preview_response.decision === "approved" ? "bg-green-600 text-white" : "bg-gloss-gold text-ink"}`}
              title={m.preview_response.note || ""} data-testid="preview-response-badge">
              {m.preview_response.decision === "approved" ? "✓ Member signed it off" : `Member requested changes${m.preview_response.note ? ": " + m.preview_response.note.slice(0, 60) : ""}`}
            </span>
          )}
        </div>
        {previewSetup && (
          <div className="mt-4 border-2 border-ink bg-white p-4 space-y-3" data-testid="preview-setup-modal">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="overline text-ink/60">What should the member see? Tick the parts to share.</p>
                <p className="font-sans text-xs text-ink/55 mt-1">Approving only signs it off — nothing goes live until you publish it.</p>
              </div>
              <button onClick={() => setPreviewSetup(null)} className="p-1 text-ink/40 hover:text-ink" data-testid="preview-setup-close"><X size={14} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {previewSetup.map((a) => (
                <label key={a.id} className={`flex items-center gap-2.5 border p-2.5 cursor-pointer ${previewTicks[a.id] ? "border-gloss-gold bg-gloss-gold/5" : "border-ink/15"}`} data-testid={`preview-tick-${a.id}`}>
                  <input type="checkbox" checked={!!previewTicks[a.id]} onChange={(e) => setPreviewTicks({ ...previewTicks, [a.id]: e.target.checked })} />
                  <span className="min-w-0">
                    <span className="block font-mono text-[9px] uppercase tracking-widest text-gloss-gold-dark">{PREVIEW_TYPE_LABEL[a.type] || a.type}</span>
                    <span className="block font-sans text-sm text-ink truncate">{a.title || a.type}</span>
                  </span>
                  <span className="font-mono text-[9px] text-ink/40 ml-auto shrink-0">{a.status === "published" ? "live" : "draft"}</span>
                </label>
              ))}
              {previewSetup.length === 0 && <p className="font-sans text-xs text-ink/50 col-span-2">No articles yet — the preview will show their cover and advert only.</p>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {[["show_directory", "Directory card", "directory"], ["show_advert", "Their advert", "advert"], ["show_notice", "Noticeboard post", "notice"], ["show_socials", "Socials & contact", "socials"]].map(([key, label, has]) => (
                <label key={key} className={`flex items-center gap-2 border p-2.5 cursor-pointer ${previewSections[key] ? "border-gloss-gold bg-gloss-gold/5" : "border-ink/15"} ${previewHas[has] === false ? "opacity-50" : ""}`} data-testid={`preview-tick-${has}`}>
                  <input type="checkbox" checked={!!previewSections[key]} onChange={(e) => setPreviewSections({ ...previewSections, [key]: e.target.checked })} />
                  <span className="font-sans text-xs text-ink">{label}{previewHas[has] === false ? " (none yet)" : ""}</span>
                </label>
              ))}
            </div>
            <button onClick={sharePreview} disabled={busy === "preview-link"} className="bg-gloss-gold text-ink px-5 py-2.5 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-50" data-testid="preview-setup-copy">
              <Eye size={13} /> {busy === "preview-link" ? "Copying…" : "Copy the preview link"}
            </button>
          </div>
        )}
      </div>

      {/* ============ 3 · Master Build (Generate whole magazine) ============ */}
      <div className="border-2 border-gloss-gold bg-gradient-to-br from-gloss-gold/10 to-transparent p-5 sm:p-6" data-testid="whole-magazine-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="overline text-gloss-gold mb-1">3 · Master Build · one click</p>
            <p className="font-serif text-xl sm:text-2xl text-ink leading-tight">Now build the whole magazine from your website scan + Master Photos.</p>
            <p className="font-sans text-sm text-ink/60 mt-2">Runs every AI step for this member — <b>portrait · editorial hero · profile · 5 Questions Q&amp;A · Advert (copy + image) · Social (post + tile) · Spotlight (article + image + 3-shot gallery) · The Edit (article + image + gallery) · 5 Questions article (+ image + gallery) · Members&apos; Desk blog (+ image + gallery)</b>. Every image is generated from your Master Photos + Face Lock so the whole magazine looks like one shoot. Anything you&apos;ve already made stays exactly as-is.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              onClick={() => generateWholeMagazine(false)}
              disabled={!!busy}
              className="bg-gloss-gold text-ink px-6 py-4 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="whole-magazine-btn"
            >
              {busy === "whole-mag" ? <RefreshCw size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {busy === "whole-mag" ? "Generating…" : "Master Build"}
            </button>
            <button
              onClick={() => generateWholeMagazine(true)}
              disabled={!!busy}
              className="border-2 border-red-700/50 text-red-700 px-4 py-2 overline text-xs flex items-center gap-2 hover:bg-red-700 hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="whole-magazine-force-btn"
              title="Wipes every existing article, image and piece of copy for this member and rebuilds from scratch."
            >
              <RefreshCw size={12} /> Force rebuild
            </button>
          </div>
        </div>

        {plan && (
          <div className="mt-4 border-2 border-ink bg-white p-4 sm:p-5 space-y-4" data-testid="photo-plan-modal">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="overline text-ink/60">The photo plan — nothing generates without your say-so</p>
                <p className="font-sans text-sm text-ink/60 mt-1">Tick what the studio should shoot. Untick to skip — you can upload your own photo in each panel below instead. Words (articles, copy) are always written; this only controls <b>images</b>.</p>
              </div>
              <button onClick={() => setPlan(null)} className="p-1 text-ink/40 hover:text-ink" data-testid="photo-plan-close"><X size={16} /></button>
            </div>

            <label className={`flex items-start gap-3 border-2 p-3 cursor-pointer transition-colors ${plan.no_ai_photos ? "border-gloss-gold bg-gloss-gold/10" : "border-ink/15"}`} data-testid="no-ai-photos-toggle">
              <input type="checkbox" checked={!!m.no_ai_photos} onChange={(e) => toggleNoAi(e.target.checked)} className="mt-1" />
              <div>
                <p className="font-sans text-sm text-ink font-medium">No AI photos for this member — ever</p>
                <p className="font-sans text-xs text-ink/55 mt-0.5">Their own Master Photos ({plan.master_photos}) are used everywhere instead. Right for businesses whose real products must be shown exactly (card machines, specific stock, regulated kit).</p>
              </div>
            </label>
            {plan.imagery_preference === "own" && (
              <p className="font-mono text-[11px] bg-gloss-gold/15 text-ink px-3 py-2" data-testid="member-own-choice-note">This member chose &quot;use my photos as they are&quot; — the studio will not generate any photos for them.</p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(plan.slots || []).map((s) => (
                <label key={s.key} className={`flex items-start gap-3 border p-3 transition-colors ${plan.own_photos_only ? "opacity-50 cursor-not-allowed border-ink/10" : planChoices[s.key] ? "border-gloss-gold bg-gloss-gold/5 cursor-pointer" : "border-ink/15 cursor-pointer"}`} data-testid={`photo-plan-slot-${s.key}`}>
                  <input type="checkbox" disabled={plan.own_photos_only} checked={!!planChoices[s.key]}
                    onChange={(e) => setPlanChoices({ ...planChoices, [s.key]: e.target.checked })} className="mt-1" data-testid={`photo-plan-tick-${s.key}`} />
                  <div className="min-w-0">
                    <p className="font-sans text-sm text-ink">{s.label}</p>
                    <p className="font-mono text-[10px] text-ink/45 mt-0.5">{s.have ? "already made — kept unless Force rebuild" : plan.own_photos_only ? "their own photos will be used" : planChoices[s.key] ? "studio will shoot this" : "skipped — upload your own below"}</p>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-ink/10">
              {plan.standalone ? (
                <button onClick={savePhotoChoices} className="bg-gloss-gold text-ink px-6 py-3 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors" data-testid="photo-plan-save">
                  <Check size={14} /> Save photo choices
                </button>
              ) : (
                <button onClick={() => startWholeMagazine(plan.force)} className="bg-gloss-gold text-ink px-6 py-3 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors" data-testid="photo-plan-start">
                  <Sparkles size={14} /> {plan.force ? "Rebuild with this plan" : "Build with this plan"}
                </button>
              )}
              <button onClick={() => setPlan(null)} className="border border-ink/30 px-4 py-3 overline text-ink/60 hover:border-ink hover:text-ink transition-colors" data-testid="photo-plan-cancel">Cancel</button>
              {plan.force && <p className="font-mono text-[10px] text-red-700 self-center">Force rebuild deletes ALL existing articles, images and copy first. Cannot be undone.</p>}
            </div>
          </div>
        )}

        {magEvent && (
          <div className="mt-4 border border-ink/15 bg-white p-3 text-sm space-y-1" data-testid="whole-magazine-result">
            {magEvent.running && (
              <p className="text-ink flex items-center gap-2 font-medium">
                <RefreshCw size={14} className="animate-spin text-gloss-gold" /> Working: {magEvent.current || "…"}
              </p>
            )}
            {magEvent.done.map((s) => (<p key={s} className="text-ink flex items-center gap-2"><Check size={14} className="text-green-600" /> Generated: {s}</p>))}
            {(magEvent.skipped || []).map((s) => (<p key={s} className="text-ink/50 flex items-center gap-2"><Check size={14} /> Kept: {s}</p>))}
            {magEvent.failed.map((s) => (<p key={s} className="text-red-600 flex items-center gap-2"><X size={14} /> {s}</p>))}
            {!magEvent.running && (magEvent.done.length + (magEvent.skipped || []).length + magEvent.failed.length) > 0 && (
              <p className="overline text-[10px] text-ink/60 pt-2 mt-1 border-t border-ink/10" data-testid="whole-mag-summary">
                {magEvent.done.length + (magEvent.skipped || []).length} of 13 items in place
                {magEvent.failed.length ? ` · ${magEvent.failed.length} failed — hit Master Build again to retry the missing ones` : " — everything is filled in. Review each panel below, edit or delete anything, then publish at your choice."}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ============ 4 · The Company File ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="company-file-panel">
        <p className="overline text-ink/60 mb-2">4 · The Company File — anything the writers should know</p>
        <p className="font-sans text-sm text-ink/60 mb-3">One place for everything: writing instructions (&quot;always call them by first name&quot;), notes from a phone call, key facts, do&apos;s and don&apos;ts, tone rules, <b>and any of their own words</b> — About Us copy, mission statement, founding story. Paste it all here. This file is read by every AI action for this member — writing, images and social.</p>
        <textarea value={m.master_instructions || ""} onChange={(e) => set("master_instructions", e.target.value)}
          rows={10} placeholder="e.g. Founder is called Sam, prefers first-name only. Never mention prices. Always highlight the family-run heritage.&#10;&#10;--- Their own words ---&#10;Paste their About Us copy, mission statement or founding story here too."
          className="w-full border border-ink/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-ink" data-testid="company-file" />
      </div>

      {/* ============ 5 · Reference documents ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="ref-docs-panel">
        <p className="overline text-ink/60 mb-2">5 · Reference documents</p>
        <p className="font-sans text-sm text-ink/60 mb-3">Upload PDFs, Word docs or text files — menus, brochures, press releases, price lists, product catalogues, past interviews, media kits. Text is extracted and fed to the writers as source material.</p>
        <label className="border border-ink/40 px-4 py-2.5 overline inline-flex items-center gap-2 cursor-pointer hover:border-ink transition-colors" data-testid="ref-doc-upload">
          {busy === "refdocs" ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />} Upload documents (PDF · DOCX · TXT)
          <input type="file" accept=".pdf,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain" multiple className="hidden" onChange={(e) => uploadRefDocs(e.target.files)} disabled={!!busy} />
        </label>
        {refDocs.length === 0 ? (
          <p className="font-mono text-[11px] text-ink/40 mt-3" data-testid="ref-docs-empty">No documents yet.</p>
        ) : (
          <ul className="mt-4 space-y-1.5" data-testid="ref-docs-list">
            {refDocs.map((d) => (
              <li key={d.id} className="flex items-center gap-2.5 border border-ink/15 px-3 py-2 text-sm">
                <FileText size={14} className="text-ink/50 shrink-0" />
                <span className="flex-1 truncate">{d.filename}</span>
                <span className="font-mono text-[10px] text-ink/40 shrink-0">{d.words ? `${d.words} words` : ""}</span>
                <button onClick={() => removeRefDoc(d.id)} disabled={!!busy} className="p-1 text-ink/40 hover:text-red-600 transition-colors" data-testid={`ref-doc-remove-${d.id}`}>
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ============ 6 · Key facts ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="key-facts-panel">
        <p className="overline text-ink/60 mb-2">6 · Key facts &amp; talking points</p>
        <p className="font-sans text-sm text-ink/60 mb-3">The non-negotiable facts every article MUST mention. One per line.</p>
        <textarea value={m.key_facts || ""} onChange={(e) => set("key_facts", e.target.value)}
          rows={6} placeholder={"- Family-run since 1985\n- Only certified organic butcher in Cardiff\n- Featured in The Times, 2024\n- Nominated for Best Independent 2025"}
          className="w-full border border-ink/30 px-4 py-3 font-mono text-[13px] focus:outline-none focus:border-ink" data-testid="key-facts" />
      </div>

      {/* ============ 7 · Business basics ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="master-basics-panel">
        <p className="overline text-ink/60 mb-3">7 · Business basics</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Business name" v={m.business_name} on={(v) => set("business_name", v)} testid="basic-business-name" />
          <Field label="Featured people — who fronts this profile" v={m.owner_name} on={(v) => set("owner_name", v)} placeholder='e.g. "Adam Lamprey" — duos welcome: "Ana & Marco Rossi"' testid="basic-owner-name" />
          <Field label="Tagline" v={m.category} on={(v) => set("category", v)} placeholder="e.g. Independent East London bakery" testid="basic-tagline" />
          <Field label="Sector" v={m.sector} on={(v) => set("sector", v)} placeholder="e.g. wholesale, trade, fintech, retail, toy distribution" testid="basic-sector" />
          <Field label="Area / borough / town" v={m.borough} on={(v) => set("borough", v)} placeholder="e.g. Shoreditch, or Cardiff" testid="basic-borough" />
          <Field label="Phone" v={m.phone} on={(v) => set("phone", v)} placeholder="+44 7000 000 000" testid="basic-phone" />
          <Field label="Mobile (from their form)" v={m.mobile} on={(v) => set("mobile", v)} placeholder="+44 7000 000 000" testid="basic-mobile" />
          <Field label="Landline (from their form)" v={m.landline} on={(v) => set("landline", v)} placeholder="020 0000 0000" testid="basic-landline" />
          <Field label="Email" v={m.email} on={(v) => set("email", v)} placeholder="hello@business.co.uk" testid="basic-email" />
          <Field label="Website" v={m.website} on={(v) => set("website", v)} placeholder="https://theirbusiness.co.uk" testid="basic-website" />
          <Field label="Online shop link (Etsy, Amazon, etc — shown separately as 'Shop')" v={m.shop_url} on={(v) => set("shop_url", v)} placeholder="https://theirshop.etsy.com" testid="basic-shop-url" />
          <div className="md:col-span-2">
            <Field label="Address" v={m.address} on={(v) => set("address", v)} placeholder="Full business address" testid="basic-address" />
          </div>
          <div className="md:col-span-2">
            <Area label="Bio" v={m.bio} on={(v) => set("bio", v)} rows={3} placeholder="A magnetic 2–3 sentence story of the business." testid="basic-bio" />
          </div>
          <div className="md:col-span-2">
            <Area label="Personal bio — about the owner (their own words from the form; the writers quote from this)" v={m.personal_bio} on={(v) => set("personal_bio", v)} rows={5}
              placeholder="Who they are, their story, where they're from — in their own words." testid="basic-personal-bio" />
          </div>
          <div className="md:col-span-2">
            <Area label="Business bio — about the business (their own words from the form)" v={m.business_bio} on={(v) => set("business_bio", v)} rows={5}
              placeholder="What the business does and what makes it special — in their own words." testid="basic-business-bio" />
          </div>
          <div className="md:col-span-2">
            <Area label="Services / products" v={m.services} on={(v) => set("services", v)} rows={4}
              placeholder={"- Bespoke wedding cakes\n- Weekly bread subscription\n- Baking classes"}
              testid="basic-services" mono />
            <p className="font-sans text-xs text-ink/45 mt-1">What they actually sell. One item per line.</p>
          </div>
          <div className="md:col-span-2">
            <Field label="Reader offer (from their form — shows as a real offer on their profile, only fill with an actual deal)" v={m.offers} on={(v) => set("offers", v)} placeholder='e.g. "10% off with code SME10"' testid="basic-offers" />
          </div>
          <div className="md:col-span-2">
            <Area label="Links & notes to the desk (from their form — press links, anything they sent)" v={m.ref_notes} on={(v) => set("ref_notes", v)} rows={4} testid="basic-ref-notes" />
          </div>
        </div>
      </div>

      {/* ============ 8 · Social handles ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="social-handles-panel">
        <p className="overline text-ink/60 mb-3">8 · Social handles</p>
        <p className="font-sans text-sm text-ink/60 mb-3">Every article ends with a &quot;how to find them&quot; block. Fill any they have; leave the rest blank.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Instagram" v={m.instagram} on={(v) => set("instagram", v)} placeholder="@handle or full URL" testid="social-instagram" />
          <Field label="TikTok" v={m.tiktok} on={(v) => set("tiktok", v)} placeholder="@handle or full URL" testid="social-tiktok" />
          <Field label="LinkedIn" v={m.linkedin} on={(v) => set("linkedin", v)} placeholder="linkedin.com/in/…" testid="social-linkedin" />
          <Field label="Facebook" v={m.facebook} on={(v) => set("facebook", v)} placeholder="facebook.com/…" testid="social-facebook" />
          <Field label="X / Twitter" v={m.x_handle} on={(v) => set("x_handle", v)} placeholder="@handle" testid="social-x" />
        </div>
      </div>

      {/* ============ 9 · Opening hours ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="opening-hours-panel">
        <p className="overline text-ink/60 mb-2">9 · Opening hours</p>
        <p className="font-sans text-sm text-ink/60 mb-3">For shops, cafés, salons and anywhere customers visit. Leave blank if it doesn&apos;t apply.</p>
        <textarea value={m.opening_hours || ""} onChange={(e) => set("opening_hours", e.target.value)}
          rows={4} placeholder={"Mon–Fri  9am – 6pm\nSat      10am – 4pm\nSun      Closed\nBank holidays by appointment"}
          className="w-full border border-ink/30 px-4 py-3 font-mono text-[13px] focus:outline-none focus:border-ink" data-testid="opening-hours" />
      </div>

      {/* ============ 10 · Logo ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="master-logo-panel">
        <p className="overline text-ink/60 mb-3">10 · Logo</p>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-32 h-20 border border-ink/15 bg-secondary flex items-center justify-center" data-testid="logo-preview">
            {m.logo
              ? <img src={logoUrl(m)} alt="logo" className="max-w-full max-h-full object-contain" style={{ filter: "invert(1)" }} />
              : <span className="font-mono text-[10px] text-ink/30">no logo</span>}
          </div>
          <label className="border border-ink/40 px-4 py-2.5 overline flex items-center gap-2 cursor-pointer hover:border-ink transition-colors" data-testid="upload-logo">
            {busy === "logo-upload" ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />} {m.logo ? "Replace logo" : "Upload logo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files)} disabled={!!busy} />
          </label>
          {m.logo && (
            <button onClick={removeLogo} disabled={!!busy} className="border border-ink/30 text-ink/60 px-4 py-2.5 overline flex items-center gap-2 hover:border-ink hover:text-ink transition-colors disabled:opacity-60" data-testid="remove-logo">
              <X size={13} /> Remove
            </button>
          )}
        </div>
        <p className="font-sans text-xs text-ink/45 mt-2">One logo only. Uploading another replaces it.</p>
      </div>

      {/* ============ Directory listing ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="directory-panel">
        <p className="overline text-ink/60 mb-2">Directory listing · live on /directory</p>
        <p className="font-sans text-sm text-ink/60 mb-4">This is exactly how they appear on the public Directory. Everything shown here is pulled from Business Basics, Social Handles and Logo above — <b>edit any of it there and it updates here instantly.</b></p>

        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-5 border border-ink/10 p-4 sm:p-5" data-testid="directory-card-preview">
          {/* Logo tile */}
          <div className="aspect-square bg-secondary border border-ink/10 flex items-center justify-center overflow-hidden">
            {m.logo ? (
              <img src={logoUrl(m)} alt={m.business_name} className="max-h-[70%] max-w-[80%] object-contain" style={{ filter: "invert(1)" }} />
            ) : (
              <span className="font-serif tracking-tight text-lg text-ink/70 text-center leading-tight uppercase px-3">{m.business_name || "No business name"}</span>
            )}
          </div>

          {/* Listing detail */}
          <div>
            <h3 className="font-serif text-2xl sm:text-3xl tracking-tight leading-none text-ink">{m.business_name || <span className="text-ink/30">Business name</span>}</h3>
            <p className="font-serif italic text-base text-ink/60 mt-1">{m.business_category || m.category || <span className="text-ink/30">Tagline</span>}</p>

            <div className="mt-4 space-y-1.5 font-sans text-sm text-ink/80">
              {m.owner_name && <p>· {m.owner_name}</p>}
              {(m.address || m.borough) && <p>· {m.address || m.borough}</p>}
              {(m.phone || m.mobile || m.landline) && <p>· {m.phone || m.mobile || m.landline}</p>}
              {m.email && <p>· {m.email}</p>}
              {m.website && <p className="truncate">· {m.website}</p>}
            </div>

            {(m.instagram || m.tiktok || m.linkedin || m.facebook || m.x_handle) && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {m.instagram && <span className="overline text-[10px] border border-ink/20 px-2 py-1">Instagram</span>}
                {m.tiktok && <span className="overline text-[10px] border border-ink/20 px-2 py-1">TikTok</span>}
                {m.linkedin && <span className="overline text-[10px] border border-ink/20 px-2 py-1">LinkedIn</span>}
                {m.facebook && <span className="overline text-[10px] border border-ink/20 px-2 py-1">Facebook</span>}
                {m.x_handle && <span className="overline text-[10px] border border-ink/20 px-2 py-1">X</span>}
              </div>
            )}

            {m.status === "published" ? (
              <a href="/directory" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 overline text-[10px] border border-ink/30 px-3 py-2 hover:border-ink hover:bg-ink hover:text-cream transition-colors" data-testid="directory-view-live">
                View live on /directory
              </a>
            ) : (
              <p className="mt-4 font-mono text-[10px] text-ink/45">· Not on the public Directory yet — Publish the member to make this listing live.</p>
            )}
          </div>
        </div>
      </div>

      {/* ============ 11 · AI Images Saved ============ */}
      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="product-shots-panel">
        <p className="overline text-ink/60 mb-2">Product Shots — their actual products</p>
        <p className="font-sans text-sm text-ink/60 mb-4">Upload their REAL products (terminals, stock, kit). The studio then combines them with the member in editorial shots — the exact product, never a generic lookalike. Results land in AI Images Saved and can be starred onto any cover.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          <label className="border-2 border-ink px-4 py-2.5 overline text-xs flex items-center gap-2 cursor-pointer hover:bg-ink hover:text-cream transition-colors" data-testid="product-upload-label">
            <Upload size={13} /> {busy === "product-upload" ? "Uploading…" : "Upload product photos"}
            <input type="file" accept="image/*" multiple className="hidden" data-testid="product-upload-input"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                setBusy("product-upload");
                const fd = new FormData();
                files.forEach((f) => fd.append("files", f));
                try {
                  const r = await client.post(`/admin/members/${m.id}/product-photos`, fd);
                  setMember({ ...m, product_photos: r.data.product_photos });
                  toast.success(`${files.length} product photo${files.length === 1 ? "" : "s"} saved — these exact products will be used.`);
                } catch { toast.error("Upload failed — try again."); }
                setBusy(""); e.target.value = "";
              }} />
          </label>
        </div>
        {(m.product_photos || []).length > 0 && (
          <>
            <input value={productScene} onChange={(e) => setProductScene(e.target.value)}
              placeholder="Optional scene — e.g. 'handing it to a café customer', 'on a marble counter'…"
              className="w-full border border-ink/20 p-3 font-sans text-sm mb-3 focus:outline-none focus:border-ink" data-testid="product-scene-input" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(m.product_photos || []).map((p, i) => (
                <div key={p} className="border border-ink/15 group" data-testid={`product-photo-${i}`}>
                  <div className="aspect-square bg-ink/5 overflow-hidden">
                    <img src={`${API}/files/${p}`} alt="product" className="w-full h-full object-contain" />
                  </div>
                  <div className="p-2 space-y-1.5">
                    <button onClick={() => combineProduct(p, true)} disabled={!!busy}
                      className="w-full bg-ink text-cream py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid={`product-combine-${i}`}>
                      {busy === `combine-${p}` ? "Shooting…" : "Combine with them"}
                    </button>
                    <div className="flex gap-1.5">
                      <button onClick={() => combineProduct(p, false)} disabled={!!busy}
                        className="flex-1 border border-ink/30 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:border-ink transition-colors disabled:opacity-50" data-testid={`product-hero-${i}`}>
                        {busy === `hero-${p}` ? "Shooting…" : "Hero shot"}
                      </button>
                      <button onClick={async () => {
                        const r = await client.delete(`/admin/members/${m.id}/product-photos?path=${encodeURIComponent(p)}`);
                        setMember({ ...m, product_photos: r.data.product_photos });
                      }} disabled={!!busy} className="border border-red-700/40 text-red-700 px-2 hover:bg-red-700 hover:text-cream transition-colors" data-testid={`product-delete-${i}`}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="font-mono text-[10px] text-ink/40 mt-2">Combining uses AI credits — one image per press. The product is copied exactly from your upload.</p>
          </>
        )}
      </div>

      <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="ai-images-saved-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
          <p className="overline text-ink/60">11 · AI Images Saved</p>
          {((m.shot_gallery || []).length > 0) && (
            <button onClick={binAllShots} disabled={!!busy} className="border border-red-700/40 text-red-700 px-3 py-1.5 overline text-[10px] flex items-center gap-1.5 hover:bg-red-700 hover:text-cream transition-colors disabled:opacity-50" data-testid="bin-all-shots">
              {busy === "bin-all" ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />} Bin all unused images
            </button>
          )}
        </div>
        <p className="font-sans text-sm text-ink/60 mb-4">Every AI portrait made anywhere in the Studio lands here. Reuse any one as their Cover Member, or delete the ones you don&apos;t want. &quot;Bin all&quot; clears everything not in live use — one click.</p>
        {((m.shot_gallery || []).length === 0) ? (
          <p className="font-mono text-[11px] text-ink/45 border border-dashed border-ink/20 p-6 text-center" data-testid="ai-images-empty">No AI images yet — generate a Cover Member portrait below to get started.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ai-images-grid">
            {[...(m.shot_gallery || [])].reverse().map((g) => {
              const isCurrent = m.directory_image === g.image;
              const usedAs = isCurrent ? "Cover Member" : inUse[g.image];
              return (
                <div key={g.id} className={`relative border ${usedAs ? "border-2 border-gloss-gold" : "border-ink/15"}`} data-testid={`saved-shot-${g.id}`}>
                  <div className="aspect-[2/3] bg-secondary overflow-hidden relative">
                    <img src={imgUrl({ cover_image: g.image })} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {usedAs && <span className="absolute top-1.5 left-1.5 bg-gloss-gold text-ink font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5" data-testid={`saved-in-use-${g.id}`}>In use</span>}
                  </div>
                  <div className="p-2 grid gap-1.5">
                    <span className="overline text-[9px] text-ink/50 truncate">{usedAs ? `· ${usedAs.toUpperCase()} ·` : ({ cover_member: "Cover Member", editorial: "Editorial", extra: "Shot" }[g.kind] || g.kind || "shot")}</span>
                    <select onChange={(e) => { if (e.target.value) { assignShotToSlot(g.image, e.target.value); e.target.value = ""; } }}
                      disabled={!!busy} defaultValue=""
                      className="overline text-[9px] border border-ink/30 py-1.5 px-1.5 bg-white cursor-pointer hover:border-ink transition-colors disabled:opacity-60" data-testid={`saved-use-in-${g.id}`}>
                      <option value="">Use in…</option>
                      <option value="directory_image">Cover Member</option>
                      <option value="cover_image">Editorial hero cover</option>
                      <option value="article:edit">The Edit cover</option>
                      <option value="article:spotlight">Spotlight cover</option>
                      <option value="article:five_questions">5 Questions cover</option>
                      <option value="article:blog">Members&apos; Desk cover</option>
                    </select>
                    <button onClick={() => deleteShot(g.id, usedAs)} disabled={!!busy} title={usedAs ? `In use as ${usedAs} — deleting removes it from there too` : "Delete permanently"} className="overline text-[10px] border border-ink/20 text-ink/60 py-1.5 hover:border-red-600 hover:text-red-600 transition-colors disabled:opacity-40" data-testid={`saved-delete-${g.id}`}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ============ Save ============ */}
      <div className="border-t border-ink/15 pt-6">
        <button onClick={saveFields} disabled={outerBusy || !!busy} className="w-full bg-ink text-cream py-4 overline flex items-center justify-center gap-2 hover:bg-gloss-silver hover:text-ink transition-colors disabled:opacity-60" data-testid="save-master">
          {outerBusy === "save" ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />} Save master section
        </button>
        <p className="font-mono text-[11px] text-ink/45 text-center mt-2">Everything above saves together. Photos, logo and reference documents save the moment you upload them.</p>
      </div>
    </section>
  );
}

const ARTICLE_LABELS = { edit: "The Edit", spotlight: "Spotlight", five_questions: "5 Questions", blog: "Members' Desk" };

const LABELS = {
  business_name: "Business name",
  tagline: "One-line pitch",
  bio: "Bio",
  borough: "Area / borough",
  services: "Services / products",
  phone: "Phone",
  email: "Email",
  address: "Address",
  website: "Website",
  instagram: "Instagram",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
};

function Field({ label, v, on, placeholder, testid }) {
  return (
    <div>
      <label className="overline text-[10px] text-ink/60 block mb-1">{label}</label>
      <input value={v || ""} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        className="w-full border border-ink/30 px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-ink" data-testid={testid} />
    </div>
  );
}

function Area({ label, v, on, rows = 4, placeholder, testid, mono }) {
  return (
    <div>
      <label className="overline text-[10px] text-ink/60 block mb-1">{label}</label>
      <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={rows} placeholder={placeholder}
        className={`w-full border border-ink/30 px-4 py-3 focus:outline-none focus:border-ink ${mono ? "font-mono text-[13px]" : "font-sans text-sm"}`} data-testid={testid} />
    </div>
  );
}
